import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InfoTooltip } from "@/components/InfoTooltip";
import { AlertCircle } from "lucide-react";
import {
  PURPOSE_CATEGORIES,
  findCategory,
  findOption,
  purposeNeedsOtherDescription,
  type PurposeSelection,
} from "@/lib/purpose";

interface Props {
  loanTitle: string;
  onLoanTitleChange: (s: string) => void;
  purpose: PurposeSelection;
  onPurposeChange: (p: PurposeSelection) => void;
}

export function PurposeSelector({ loanTitle, onLoanTitleChange, purpose, onPurposeChange }: Props) {
  const category = findCategory(purpose.categoryId);
  const option = findOption(purpose.categoryId, purpose.specificId);
  const needsOther = purposeNeedsOtherDescription(purpose);

  const setCategory = (id: string) => {
    onPurposeChange({ categoryId: id, specificId: null, otherDescription: '' });
  };
  const setSpecific = (id: string) => {
    onPurposeChange({ ...purpose, specificId: id });
  };
  const setOther = (s: string) => {
    onPurposeChange({ ...purpose, otherDescription: s });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Loan Purpose</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="purpose-loan-name">
              Loan Name
              <InfoTooltip content="A short name to identify this loan (e.g., '2026 Vehicle Purchase'). Appears on saved scenarios and reports." />
            </Label>
            <Input
              id="purpose-loan-name"
              value={loanTitle}
              onChange={(e) => onLoanTitleChange(e.target.value)}
              placeholder="e.g., 2026 Vehicle Purchase"
            />
          </div>
          <div>
            <Label>
              Purpose Category
              <InfoTooltip content="Choose the broad category that best describes why you are borrowing." />
            </Label>
            <Select value={purpose.categoryId ?? undefined} onValueChange={setCategory}>
              <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
              <SelectContent>
                {PURPOSE_CATEGORIES.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>
              Specific Purpose
              <InfoTooltip content="Choose the specific use of funds within the selected category." />
            </Label>
            <Select
              value={purpose.specificId ?? undefined}
              onValueChange={setSpecific}
              disabled={!category}
            >
              <SelectTrigger><SelectValue placeholder={category ? "Select a purpose" : "Select category first"} /></SelectTrigger>
              <SelectContent>
                {category?.options.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {option?.isOther && (
          <div>
            <Label htmlFor="purpose-other">
              Describe the purpose
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Textarea
              id="purpose-other"
              value={purpose.otherDescription}
              onChange={(e) => setOther(e.target.value)}
              placeholder="Briefly describe the purpose of this loan."
              className="min-h-[60px]"
              maxLength={400}
            />
            {needsOther && (
              <p className="mt-1 text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Description required before generating a report.
              </p>
            )}
          </div>
        )}

        {category?.note && (
          <div className="rounded-md border border-accent/30 bg-accent/5 p-3 text-xs leading-relaxed text-muted-foreground">
            {category.note}
          </div>
        )}
      </CardContent>
    </Card>
  );
}